import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "detail-view" }
const _hoisted_2 = { class: "content-layout" }
const _hoisted_3 = { class: "main-content-wrapper" }
const _hoisted_4 = {
  key: 2,
  class: "related-games-inline-wrapper"
}
const _hoisted_5 = {
  key: 8,
  class: "mobile-back-container"
}

import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useBetslipStore } from "../stores/betslip";
import { useGameStore } from "../stores/game";
import { useToast } from "../composables/useToast";
import { useModalStore } from "../stores/modal";
import type { BetslipItem } from "../stores/betslip";
import BetSlip from "../components/BetSlip.vue";
import MobileDrawsSection from "../components/mobile/MobileDrawsSection.vue";
import DesktopDrawsSection from "../components/desktop/DesktopDrawsSection.vue";
import MobileBettingSection from "../components/mobile/MobileBettingSection.vue";
import MobileConfirmSection from "../components/mobile/MobileConfirmSection.vue";
import MobileHeader from "../components/mobile/MobileHeader.vue";
import DesktopHeader from "../components/desktop/DesktopHeader.vue";
import DesktopBettingSection from "../components/desktop/DesktopBettingSection.vue";
import DesktopConfirmSection from "../components/desktop/DesktopConfirmSection.vue";
import RelatedGamesSelector from "../components/RelatedGamesSelector.vue";
import { useRelatedGames } from "../composables/useRelatedGames";
import { useRelatedGameMatching } from "../composables/useRelatedGameMatching";

interface Game {
  id: string;
  name: string;
  slug: string;
  flagUrl: string;
  drawTime: Date;
  cutoffTime: Date;
  country: string;
  draw_date?: string;
  cutoff?: string;
  game_name?: string;
  game_slug?: string;
  game_logo?: string;
}

interface MarketOutcome {
  id: number;
  text: string;
  select_numbers: boolean;
  pick: number;
  slug: string;
  odds: string;
  specifier: string | null;
}

interface Market {
  id: number;
  name: string;
  slug: string;
  description: string;
  ordering: number;
  outcomes: MarketOutcome[];
}

interface SelectedMarket {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
}

interface MarketGroup {
  id: number;
  name: string;
  slug: string;
  ordering: number;
  has_bonus: boolean;
  markets: Market[];
  rules: Array<{
    id: number;
    name: string;
    balls_min: number;
    balls_max: number;
    picks_min: number;
    picks_max: number;
    has_bonus: number | boolean;
    max_resulted: number;
    type: string;
  }>;
}

interface Line {
  numbers: number[];
  bonus?: number;
  numBets: number;
  picks: number;
  market?: SelectedMarket;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DetailView',
  emits: ['openBetslipModal', 'drawsChanged'],
  setup(__props, { expose: __expose, emit: __emit }) {

const route = useRoute();
const router = useRouter();
const gameStore = useGameStore();
const { showToast } = useToast();
const modalStore = useModalStore();
const emit = __emit;

const lottery = ref<Game | null>(null);
const selectedDrawsArray = ref<boolean[]>([]);
const selectedMarket = ref<SelectedMarket | null>(null);
const mobileActiveTab = ref<'main' | 'bonus'>('main');
const isMobile = ref(window.innerWidth <= 768);
const lines = ref<Line[]>([]);
const bettingMarkets = ref<MarketGroup[]>([]);
const mobileViewBetsRef = ref<HTMLElement | null>(null);
const betslipWrapperRef = ref<HTMLElement | null>(null);
const isClearingAfterSubmission = ref(false);
const lastAddedBet = ref<BetslipItem | null>(null);
const selectedRelatedGames = ref<string[]>([]);
const { getRelatedGamesSync } = useRelatedGames();
const { findMatchingBet, createRelatedBet } = useRelatedGameMatching();

const hasRelatedGamesForLottery = computed(() => {
  if (!lottery.value) return false;
  const related = getRelatedGamesSync(lottery.value.slug);
  return Array.isArray(related) && related.length > 0;
});

const countdown = ref({
  hours: 0,
  minutes: 0,
  seconds: 0
});

const updateCountdown = () => {
  const validDraws = gameStore.getValidDraws;
  if (!validDraws || validDraws.length === 0) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 };
    return;
  }

  const earliestDraw = validDraws.reduce((earliest, draw) => {
    const cutoffTime = new Date(draw.cutoff || draw.draw_date);
    const earliestTime = new Date(earliest.cutoff || earliest.draw_date);
    return cutoffTime < earliestTime ? draw : earliest;
  }, validDraws[0]);
  
  try {
    const now = new Date();
    const cutoffRaw = earliestDraw.cutoff || earliestDraw.draw_date;
    
    let cutoffTime: Date;
    if (typeof cutoffRaw === 'string') {
      const dateStr = cutoffRaw.includes('T') ? cutoffRaw : cutoffRaw.replace(' ', 'T');
      cutoffTime = new Date(dateStr);
    } else {
      cutoffTime = new Date(cutoffRaw);
    }
    
    if (isNaN(cutoffTime.getTime())) {
      countdown.value = { hours: 0, minutes: 0, seconds: 0 };
      return;
    }
    
    const diff = cutoffTime.getTime() - now.getTime();

    if (diff <= 0) {
      countdown.value = { hours: 0, minutes: 0, seconds: 0 };
      return;
    }

    countdown.value = {
      hours: Math.floor(diff / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000)
    };
  } catch (error) {
    console.error('Error updating countdown:', error);
    countdown.value = { hours: 0, minutes: 0, seconds: 0 };
  }
};

let countdownTimer: number;

const betslipStore = useBetslipStore();
const { addBet, removeBet: removeBetFromSlip, clearBetslip } = betslipStore;
const betslip = computed(() => betslipStore.betslip);

watch(
  () => route.params.slug,
  async (newSlug) => {
    if (newSlug) {
      try {
        await gameStore.fetchGameDetails(newSlug as string)
        const gameDetail = gameStore.getGameDetail;
        if (gameDetail) {
          lottery.value = {
            id: gameDetail.id.toString(),
            name: gameDetail.name,
            slug: gameDetail.slug,
            flagUrl: gameDetail.logo,
            drawTime: new Date(gameDetail.current_draw.draw_date),
            cutoffTime: new Date(gameDetail.current_draw.cutoff),
            country: gameDetail.game_type.name,
            draw_date: gameDetail.current_draw.draw_date,
            cutoff: gameDetail.current_draw.cutoff,
            game_name: gameDetail.name,
            game_slug: gameDetail.slug,
            game_logo: gameDetail.logo
          };

          const nextDraws = gameStore.getValidDraws || [];
          selectedDrawsArray.value = new Array(nextDraws.length).fill(false);
          if (nextDraws.length > 0) {
            selectedDrawsArray.value[0] = true;
          }

          const marketGroups = gameStore.getMarketGroups || [];
          bettingMarkets.value = marketGroups.map(group => ({
            id: group.id,
            name: group.name,
            slug: group.slug,
            ordering: group.ordering,
            has_bonus: group.has_bonus,
            rules: group.rules,
            markets: group.markets.map(market => ({
              id: market.id,
              name: market.name,
              slug: market.slug,
              description: market.description,
              ordering: market.ordering,
              outcomes: market.outcomes || []
            }))
          }));
        }
      } catch (error) {
        console.error('Error fetching game details:', error);
        router.push('/next-draws');
      }
    }
  },
  { immediate: true }
);

watch(
  () => route.query,
  () => {
    if (route.params.slug) {
      gameStore.fetchGameDetails(route.params.slug as string)
    }
  },
  { deep: true }
);

watch(
  () => selectedDrawsArray.value,
  (newSelection) => {
    const selectedDraws = gameStore.getValidDraws.filter((_, index) => newSelection[index]);
    emit('drawsChanged', selectedDraws);
  },
  { deep: true }
);

watch(
  () => lottery.value,
  (newLottery) => {
    if (countdownTimer) {
      clearInterval(countdownTimer);
    }
    
    if (newLottery?.cutoff) {
      updateCountdown();
      countdownTimer = window.setInterval(updateCountdown, 1000);
    }
  },
  { immediate: true }
);

watch(
  () => gameStore.getValidDraws,
  (newDraws) => {
    if (countdownTimer) {
      clearInterval(countdownTimer);
    }
    
    if (newDraws && newDraws.length > 0) {
      updateCountdown();
      countdownTimer = window.setInterval(updateCountdown, 1000);
    }
  },
  { immediate: true }
);


const openBetslip = () => {
  modalStore.openBetslip();
};

const goBackToLotteries = () => {
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push({ name: 'next-draws' });
  }
};

const clearAllLines = () => {
  lines.value = [];
  selectedRelatedGames.value = [];
};

const handleClearedAfterSubmission = () => {
  isClearingAfterSubmission.value = true;
  lines.value = [];
  mobileActiveTab.value = 'main';
  
  const nextDraws = gameStore.getValidDraws || [];
  selectedDrawsArray.value = new Array(nextDraws.length).fill(false);
  if (nextDraws.length > 0) {
    selectedDrawsArray.value[0] = true;
  }
};

const scrollToViewBetsButton = () => {
  if (isMobile.value && mobileViewBetsRef.value) {
    mobileViewBetsRef.value.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center' 
    });
  }
};

const scrollToBetslip = () => {
  if (!isMobile.value && betslipWrapperRef.value) {
    betslipWrapperRef.value.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  }
};

const selectDraws = (drawCount: number) => {
  const availableDraws = gameStore.getValidDraws || [];
  const maxDraws = Math.min(availableDraws.length, drawCount);
  
  selectedDrawsArray.value = new Array(availableDraws.length).fill(false);
  
  for (let i = 0; i < maxDraws; i++) {
    selectedDrawsArray.value[i] = true;
  }
};

const handleSelectDraws = (drawData: {
  betIndex: number;
  drawCount: number;
  selectedDraws: Array<{draw_date: string, cutoff: string, event_id: number}>;
  gameId: string | number;
}) => {
  if (betslip.value.length === 0 || isClearingAfterSubmission.value) {
    return;
  }
  
  const currentGameId = gameStore.getGameDetail?.id;
  if (currentGameId && drawData.gameId === currentGameId) {
    const availableDraws = gameStore.getValidDraws || [];
    selectedDrawsArray.value = new Array(availableDraws.length).fill(false);
    
    for (let i = 0; i < Math.min(drawData.drawCount, availableDraws.length); i++) {
      selectedDrawsArray.value[i] = true;
    }
  }
};

const handleDrawsChangedFromBetSlip = (selectedDraws: Array<{draw_date: string, cutoff: string, event_id: number}>) => {
  if (betslip.value.length === 0 || isClearingAfterSubmission.value) {
    return;
  }
  
  const availableDraws = gameStore.getValidDraws || [];
  selectedDrawsArray.value = new Array(availableDraws.length).fill(false);
  
  selectedDraws.forEach(draw => {
    const index = availableDraws.findIndex(availableDraw => 
      availableDraw.event_id === draw.event_id || 
      availableDraw.draw_date === draw.draw_date
    );
    if (index !== -1) {
      selectedDrawsArray.value[index] = true;
    }
  });
};

const toggleDrawSelection = (index: number) => {
  selectedDrawsArray.value[index] = !selectedDrawsArray.value[index];
};

const handleUpdateSelectedDraws = (index: number, checked: boolean) => {
  selectedDrawsArray.value[index] = checked;
};

const expandedGroupIndex = ref<number | null>(0);
const toggleGroup = (idx: number) => {
  expandedGroupIndex.value = expandedGroupIndex.value === idx ? null : idx;
};

const onMobileTabChanged = (tab: 'main' | 'bonus') => {
  mobileActiveTab.value = tab;
};

const selectMarket = async (bet: {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
}) => {
  if (!bet) return;
  
  if (bet.select_numbers) {
    selectedMarket.value = bet;
    if (isMobile.value) {
      mobileActiveTab.value = 'main';
      onMobileTabChanged('main');
    }
  } else {
    selectedMarket.value = null;
    const selectedDrawDates = gameStore.getValidDraws.filter((_, index) => selectedDrawsArray.value[index]);
    
    let betType = bet.label;
    let marketDescription = bet.market.description;
    const outcome = bet.market.outcomes.find(o => o.id.toString() === bet.outcome_id);
    if (outcome?.specifier) {
      betType = bet.label.replace('{value}', outcome.specifier);
      marketDescription = bet.market.description.replace('{value}', outcome.specifier);
    }
    
    const betslipItem: BetslipItem = {
      market_id: bet.market.id,
      market_group_id: bet.marketGroup.id,
      game_id: gameStore.getGameDetail?.id || 0,
      game_name: gameStore.getGameDetail?.name,
      draws: selectedDrawDates,
      allAvailableDraws: gameStore.getValidDraws || [],
      drawCount: selectedDrawDates.length,
      numbers: [],
      stake: Number(gameStore.getBetSettings?.bet_default) ?? 1,
      betType: betType,
      maxStake: Number(gameStore.getBetSettings?.bet_limit) ?? 10000,
      odds: bet.odds,
      numBets: 1,
      picks: bet.balls,
      outcome_id: bet.outcome_id,
      label: bet.label,
      market_name: bet.market.name,
      market_description: marketDescription,
      specifier: outcome?.specifier || null,
      currency_symbol: gameStore.getBetSettings?.currency_symbol,
    };

    let duplicateBets = 0;

    const betAdded = addBet(betslipItem);
    
    if (!betAdded) {
      duplicateBets++;
      showToast({
        title: 'Bet Already Added',
        message: 'This bet is already in your betslip.',
        type: 'info',
        duration: 3000
      });
    } else {
      lastAddedBet.value = betslipItem;

      if (selectedRelatedGames.value.length > 0 && lottery.value && hasRelatedGamesForLottery.value) {
        const relatedGames = getRelatedGamesSync(lottery.value.slug);
        const selectedGames = relatedGames.filter(g => selectedRelatedGames.value.includes(g.slug));

        for (const relatedGame of selectedGames) {
          try {
            const matchingResult = await findMatchingBet(relatedGame, betslipItem);
            const relatedBet = createRelatedBet(matchingResult, betslipItem);
            const added = addBet(relatedBet);
            if (!added) {
              duplicateBets++;
            }
          } catch (error) {
            console.error(`Error adding bet to ${relatedGame.name}:`, error);
          }
        }
      }

      if (duplicateBets > 0) {
        showToast({
          title: 'Duplicate Bets',
          message: `${duplicateBets} bet(s) were already in your betslip and were skipped.`,
          type: 'info',
          duration: 3000
        });
      }

      selectedRelatedGames.value = [];

      modalStore.closeBetslip();
      setTimeout(() => {
        if (isMobile.value) {
          scrollToViewBetsButton();
        } else {
          scrollToBetslip();
        }
      }, 100);
    }
  }
};

const removeMarket = (bet: {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
}) => {
  const betToRemove = betslip.value.find(item => 
    item.outcome_id === bet.outcome_id &&
    item.market_id === bet.market.market_id
  );
  
  if (betToRemove) {
    removeBetFromSlip(betslip.value.indexOf(betToRemove));
    showToast({
      title: 'Bet Removed',
      message: `${bet.label} has been removed from your betslip.`,
      type: 'info',
      duration: 2000
    });
  }
};

const addLine = (line: Line) => {
  if (!line || !selectedMarket.value) {
    console.error("No market selected when adding line or line is invalid");
    return;
  }
  
  const lineWithMarket = {
    ...line,
    market: selectedMarket.value
  };
  lines.value.push(lineWithMarket);
};

const removeLine = (index: number) => {
  lines.value.splice(index, 1);
};

const isPlacingBet = ref(false);

const addToBetslip = async (mobileLines?: Line[]) => {
  if (isPlacingBet.value) return;
  
  const linesToProcess = mobileLines || lines.value;
  
  if (selectedMarket.value === null || linesToProcess.length === 0) {
    console.error("No market or lines selected");
    return;
  }

  if (!selectedDrawsArray.value.some((selected) => selected)) {
    if (gameStore.getValidDraws.length > 0) {
      selectedDrawsArray.value[0] = true;
    } else {
      console.error("No draws available");
      return;
    }
  }

  const selectedDrawDates = gameStore.getValidDraws.filter((_, index) => selectedDrawsArray.value[index]);
  if (!selectedDrawDates.length) {
    console.error("No draw date selected");
    return;
  }

  let duplicateBets = 0;
  const successfullyAddedBets: BetslipItem[] = [];
  let maxBetsReached = false;
  
  for (const line of linesToProcess) {
    if (maxBetsReached) {
      break;
    }

    const lineMarket = line.market || selectedMarket.value;
    if (!lineMarket) {
      console.error("No market information found for line");
      continue;
    }
    
    const isBonusOnly = lineMarket.marketGroup.slug === 'bonus-ball-only-additional-drum' || 
                       lineMarket.marketGroup.slug === 'bonus-ball-only-same-drum';
    const isMainIncludingBonus = lineMarket.marketGroup.slug === 'standard-bet-bonus-from-same-drum';
    const numbersPayload = [
      isBonusOnly ? [] : (Array.isArray(line.numbers) ? line.numbers : []),
      (!isMainIncludingBonus && line.bonus !== undefined && line.bonus !== null) ? [line.bonus] : []
    ];
    const bet = {
      market_id: lineMarket.market.id,
      market_group_id: lineMarket.marketGroup.id,
      game_name: gameStore.getGameDetail?.name,
      game_id: gameStore.getGameDetail?.id || 0,
      numbers: numbersPayload,
      bonus: line.bonus,
      stake: Number(gameStore.getBetSettings?.bet_default) ?? 1,
      draws: selectedDrawDates,
      allAvailableDraws: gameStore.getValidDraws || [],
      drawCount: selectedDrawDates.length,
      betType: lineMarket.label,
      maxStake: Number(gameStore.getBetSettings?.bet_limit) ?? 10000,
        odds: lineMarket.odds,
        picks: isBonusOnly ? 1 : (line.picks || lineMarket.balls),
        numBets: 1,
        label: lineMarket.label,
        market_name: lineMarket.market.name,
        market_description: lineMarket.market.description,
        outcome_id: lineMarket.outcome_id,
      currency_symbol: gameStore.getBetSettings?.currency_symbol,
    };

    const betAdded = addBet(bet);
    if (!betAdded) {
      duplicateBets++;
    } else {
      successfullyAddedBets.push(bet);
      lastAddedBet.value = bet;
    }
  }

  // Process related games for all successfully added bets
  if (selectedRelatedGames.value.length > 0 && successfullyAddedBets.length > 0 && lottery.value) {
    const relatedGames = getRelatedGamesSync(lottery.value.slug);
    const selectedGames = relatedGames.filter(g => selectedRelatedGames.value.includes(g.slug));
    
    // Process each successfully added bet
    for (const addedBet of successfullyAddedBets) {
      // Process each selected related game for this bet
      for (const relatedGame of selectedGames) {
        try {
          const matchingResult = await findMatchingBet(relatedGame, addedBet);
          const relatedBet = createRelatedBet(matchingResult, addedBet);
          const added = addBet(relatedBet);
          if (!added) {
            duplicateBets++;
          }
        } catch (error) {
          console.error(`Error adding bet to ${relatedGame.name}:`, error);
        }
      }
    }
  }
  
  if (duplicateBets > 0) {
    showToast({
      title: 'Duplicate Bets',
      message: `${duplicateBets} bet(s) were already in your betslip and were skipped.`,
      type: 'info',
      duration: 3000
    });
  }

  lines.value = [];
  selectedRelatedGames.value = [];
  mobileActiveTab.value = 'main';
  
  setTimeout(() => {
    if (isMobile.value) {
      scrollToViewBetsButton();
    } else {
      scrollToBetslip();
    }
  }, 100);
};


let drawsRefreshTimer: number | null = null;

const setupDrawRefreshTimer = () => {
  const validDraws = gameStore.getValidDraws;
  if (validDraws.length === 0) return;

  const earliestCutoff = validDraws.reduce((earliest, draw) => {
    const cutoffTime = new Date(draw.cutoff || draw.draw_date);
    return cutoffTime < earliest ? cutoffTime : earliest;
  }, new Date(validDraws[0].cutoff || validDraws[0].draw_date));

  const now = new Date();
  const timeUntilExpiry = earliestCutoff.getTime() - now.getTime();

  if (timeUntilExpiry > 0) {
    if (drawsRefreshTimer) {
      clearTimeout(drawsRefreshTimer);
    }

    drawsRefreshTimer = window.setTimeout(() => {
      gameStore.refreshDraws().then(() => {
        if (countdownTimer) {
          clearInterval(countdownTimer);
        }
        updateCountdown();
        countdownTimer = window.setInterval(updateCountdown, 1000);
        setupDrawRefreshTimer();
      });
    }, timeUntilExpiry + 1000);
  }
};

onMounted(async () => {
  window.addEventListener("resize", handleResize);
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
  setupDrawRefreshTimer();
});

watch(() => route.path, () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
});

watch(() => gameStore.getValidDraws, (newDraws, oldDraws) => {
  if (newDraws.length !== oldDraws?.length) {
    const newSelection = new Array(newDraws.length).fill(false);
    
    if (oldDraws && oldDraws.length > 0) {
      oldDraws.forEach((oldDraw: { event_id: string | number }, oldIndex: number) => {
        if (selectedDrawsArray.value[oldIndex]) {
          const matchingIndex = newDraws.findIndex((newDraw: { event_id: string | number }) => 
            newDraw.event_id === oldDraw.event_id
          );
          if (matchingIndex !== -1) {
            newSelection[matchingIndex] = true;
          }
        }
      });
    }
    
    if (!newSelection.some(Boolean) && newDraws.length > 0) {
      newSelection[0] = true;
    }
    
    selectedDrawsArray.value = newSelection;
    setupDrawRefreshTimer();
  }
}, { immediate: true });

watch(() => betslip.value.length, (newLength, oldLength) => {
  if (oldLength && oldLength > 0 && newLength === 0) {
    if (!isClearingAfterSubmission.value) {
      selectedMarket.value = null;
      mobileActiveTab.value = 'main';
    }
    isClearingAfterSubmission.value = false;
  }
});

watch(() => modalStore.showBetslip, (isOpen) => {
  if (!isOpen && isMobile.value) {
    mobileActiveTab.value = 'main';
    lines.value = [];
  }
});

let resizeTimeout: number | null = null;
const handleResize = () => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }
  resizeTimeout = window.setTimeout(() => {
    const newIsMobile = window.innerWidth <= 768;
    if (newIsMobile !== isMobile.value) {
      selectedMarket.value = null;
      lines.value = [];
      mobileActiveTab.value = 'main';
      isMobile.value = newIsMobile;
    }
  }, 100);
};

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }
  if (countdownTimer) {
    clearInterval(countdownTimer);
  }
  if (drawsRefreshTimer) {
    clearTimeout(drawsRefreshTimer);
  }
});


__expose({
  selectDraws
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isMobile.value)
      ? (_openBlock(), _createBlock(MobileHeader, {
          key: 0,
          lottery: lottery.value,
          countdown: countdown.value,
          onGoBack: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).back()))
        }, null, 8, ["lottery", "countdown"]))
      : (!isMobile.value && lottery.value)
        ? (_openBlock(), _createBlock(DesktopHeader, {
            key: 1,
            lottery: lottery.value,
            countdown: countdown.value,
            onGoBack: _cache[1] || (_cache[1] = ($event: any) => (_unref(router).back()))
          }, null, 8, ["lottery", "countdown"]))
        : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (isMobile.value)
          ? (_openBlock(), _createBlock(MobileDrawsSection, {
              key: 0,
              draws: _unref(gameStore).getValidDraws,
              "selected-draws": selectedDrawsArray.value,
              onUpdateSelectedDraws: handleUpdateSelectedDraws,
              onToggleDrawSelection: toggleDrawSelection
            }, null, 8, ["draws", "selected-draws"]))
          : (_openBlock(), _createBlock(DesktopDrawsSection, {
              key: 1,
              draws: _unref(gameStore).getValidDraws,
              "selected-draws": selectedDrawsArray.value,
              onUpdateSelectedDraws: handleUpdateSelectedDraws,
              onToggleDrawSelection: toggleDrawSelection
            }, null, 8, ["draws", "selected-draws"])),
        (lottery.value && hasRelatedGamesForLottery.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _cache[7] || (_cache[7] = _createElementVNode("div", { class: "related-games-header" }, [
                _createElementVNode("span", { class: "related-games-title" }, "Also add to: ")
              ], -1)),
              _createVNode(RelatedGamesSelector, {
                "current-game-slug": lottery.value.slug,
                "selected-games": selectedRelatedGames.value,
                "is-processing": isPlacingBet.value,
                "onUpdate:selectedGames": _cache[2] || (_cache[2] = ($event: any) => (selectedRelatedGames.value = $event))
              }, null, 8, ["current-game-slug", "selected-games", "is-processing"])
            ]))
          : _createCommentVNode("", true),
        (isMobile.value)
          ? (_openBlock(), _createBlock(MobileBettingSection, {
              key: `mobile-betting-${isMobile.value}`,
              "betting-markets": bettingMarkets.value,
              "selected-market": selectedMarket.value,
              "hot-numbers": _unref(gameStore).getHotNumbers,
              "cold-numbers": _unref(gameStore).getColdNumbers,
              "betslip-length": betslip.value.length,
              onSelectMarket: selectMarket,
              onRemoveMarket: removeMarket,
              onToggleGroup: toggleGroup,
              onTabChanged: onMobileTabChanged,
              onAddLine: addLine,
              onRemoveLine: removeLine,
              onAddToBetslip: _cache[3] || (_cache[3] = (mobileLines) => addToBetslip(mobileLines)),
              onClearAll: clearAllLines
            }, null, 8, ["betting-markets", "selected-market", "hot-numbers", "cold-numbers", "betslip-length"]))
          : _createCommentVNode("", true),
        (!isMobile.value)
          ? (_openBlock(), _createBlock(DesktopBettingSection, {
              key: `desktop-betting-${!isMobile.value}`,
              "betting-markets": bettingMarkets.value,
              "selected-market": selectedMarket.value,
              "hot-numbers": _unref(gameStore).getHotNumbers,
              "cold-numbers": _unref(gameStore).getColdNumbers,
              "betslip-length": betslip.value.length,
              onSelectMarket: selectMarket,
              onRemoveMarket: removeMarket,
              onToggleGroup: toggleGroup,
              onTabChanged: onMobileTabChanged,
              onAddLine: addLine,
              onRemoveLine: removeLine,
              onAddToBetslip: _cache[4] || (_cache[4] = (desktopLines) => addToBetslip(desktopLines)),
              onClearAll: clearAllLines
            }, null, 8, ["betting-markets", "selected-market", "hot-numbers", "cold-numbers", "betslip-length"]))
          : _createCommentVNode("", true),
        (isMobile.value && selectedMarket.value && selectedMarket.value.select_numbers && mobileActiveTab.value === 'main')
          ? (_openBlock(), _createBlock(MobileConfirmSection, {
              key: `mobile-confirm-${isMobile.value}`,
              lines: lines.value,
              "current-game-slug": lottery.value?.slug,
              "selected-related-games": selectedRelatedGames.value,
              "is-processing": isPlacingBet.value,
              onClearAll: clearAllLines,
              onRemoveLine: removeLine,
              onAddToBetslip: addToBetslip,
              "onUpdate:selectedRelatedGames": _cache[5] || (_cache[5] = ($event: any) => (selectedRelatedGames.value = $event))
            }, null, 8, ["lines", "current-game-slug", "selected-related-games", "is-processing"]))
          : _createCommentVNode("", true),
        (!isMobile.value && selectedMarket.value && selectedMarket.value.select_numbers)
          ? (_openBlock(), _createBlock(DesktopConfirmSection, {
              key: `desktop-confirm-${!isMobile.value}`,
              lines: lines.value,
              "current-game-slug": lottery.value?.slug,
              "selected-related-games": selectedRelatedGames.value,
              "is-processing": isPlacingBet.value,
              onClearAll: clearAllLines,
              onRemoveLine: removeLine,
              onAddToBetslip: addToBetslip,
              "onUpdate:selectedRelatedGames": _cache[6] || (_cache[6] = ($event: any) => (selectedRelatedGames.value = $event))
            }, null, 8, ["lines", "current-game-slug", "selected-related-games", "is-processing"]))
          : _createCommentVNode("", true),
        (isMobile.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 7,
              class: "mobile-view-bets-container",
              ref_key: "mobileViewBetsRef",
              ref: mobileViewBetsRef
            }, [
              _createElementVNode("button", {
                class: "mobile-view-bets",
                onClick: openBetslip
              }, " View Bets (" + _toDisplayString(betslip.value.length) + ") ", 1)
            ], 512))
          : _createCommentVNode("", true),
        (isMobile.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("button", {
                class: "mobile-back-button",
                onClick: goBackToLotteries
              }, _cache[8] || (_cache[8] = [
                _createElementVNode("svg", {
                  class: "mobile-back-icon",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  "stroke-width": "2"
                }, [
                  _createElementVNode("path", { d: "M19 12H5M12 19l-7-7 7-7" })
                ], -1),
                _createElementVNode("span", { class: "mobile-back-text" }, "BACK TO LOTTERIES", -1)
              ]))
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", {
        class: "betslip-wrapper",
        ref_key: "betslipWrapperRef",
        ref: betslipWrapperRef
      }, [
        _createVNode(BetSlip, {
          onRemoveBet: _unref(removeBetFromSlip),
          onClear: _unref(clearBetslip),
          onClearedAfterSubmission: handleClearedAfterSubmission,
          onSelectDraws: handleSelectDraws,
          onDrawsChanged: handleDrawsChangedFromBetSlip
        }, null, 8, ["onRemoveBet", "onClear"])
      ], 512)
    ])
  ]))
}
}

})